import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';
import { useToast } from '@/lib/hooks/useToast';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

// ── Company internal documents (CR #103) ────────────────────────────────────
// Insurance / WorkSafe / CRA / legal / banking papers in one place, expiry-
// tracked. Rows are uploads OR live pointers at mail attachments (seeded from
// the mailbox: CGL policy, payment schedule, signed proposal, void cheque…).

interface CompanyDoc {
  id: number;
  title: string;
  category: 'insurance' | 'worksafe' | 'cra' | 'legal' | 'banking' | 'other';
  file_path: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  mail_message_id: number | null;
  mail_att_idx: number | null;
  expiry_date: string | null;
  notes: string | null;
}

const CATEGORIES: { id: CompanyDoc['category']; label: string; icon: string }[] = [
  { id: 'insurance', label: 'Insurance', icon: 'verified_user' },
  { id: 'worksafe', label: 'WorkSafeBC', icon: 'health_and_safety' },
  { id: 'cra', label: 'CRA / Tax', icon: 'account_balance' },
  { id: 'legal', label: 'Legal', icon: 'gavel' },
  { id: 'banking', label: 'Banking', icon: 'account_balance_wallet' },
  { id: 'other', label: 'Other', icon: 'folder' },
];

const daysUntil = (iso: string | null) => {
  if (!iso) return null;
  return Math.ceil((new Date(`${iso}T12:00:00`).getTime() - Date.now()) / 86400000);
};

export function CompanyDocsPage() {
  const { toast } = useToast();
  const [docs, setDocs] = useState<CompanyDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<CompanyDoc | null>(null);
  const [editingExpiry, setEditingExpiry] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [fTitle, setFTitle] = useState('');
  const [fCategory, setFCategory] = useState<CompanyDoc['category']>('insurance');
  const [fExpiry, setFExpiry] = useState('');
  const [fNotes, setFNotes] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/company-docs');
      setDocs(r.data.data ?? []);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const byCategory = useMemo(() =>
    CATEGORIES.map(c => ({ ...c, docs: docs.filter(d => d.category === c.id) })).filter(c => c.docs.length > 0),
    [docs]);

  const expiring = docs.filter(d => {
    const days = daysUntil(d.expiry_date);
    return days !== null && days <= 60;
  });

  const openDoc = async (d: CompanyDoc) => {
    if (d.file_path) { window.open(`/${d.file_path}`, '_blank'); return; }
    if (d.mail_message_id !== null && d.mail_att_idx !== null) {
      try {
        const token = localStorage.getItem('accessToken');
        const r = await fetch(`/api/mail/messages/${d.mail_message_id}/attachments/${d.mail_att_idx}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error();
        window.open(URL.createObjectURL(await r.blob()), '_blank');
      } catch { toast.error('Could not open — the source email may have moved'); }
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fTitle.trim()) { toast.error('Give the document a title'); return; }
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Pick a file to upload'); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', fTitle.trim());
      form.append('category', fCategory);
      if (fExpiry) form.append('expiry_date', fExpiry);
      if (fNotes.trim()) form.append('notes', fNotes.trim());
      await apiClient.post('/company-docs', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Filed');
      setShowAdd(false);
      setFTitle(''); setFExpiry(''); setFNotes('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch { toast.error('Upload failed'); }
    finally { setSaving(false); }
  };

  const saveExpiry = async (d: CompanyDoc, expiry: string) => {
    setEditingExpiry(null);
    try {
      await apiClient.put(`/company-docs/${d.id}`, { expiry_date: expiry || null });
      setDocs(prev => prev.map(x => x.id === d.id ? { ...x, expiry_date: expiry || null } : x));
    } catch { toast.error('Failed to save'); }
  };

  const doDelete = async (d: CompanyDoc) => {
    setConfirmDelete(null);
    try {
      await apiClient.delete(`/company-docs/${d.id}`);
      setDocs(prev => prev.filter(x => x.id !== d.id));
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <MainLayout>
      <LargeTitleHeader
        title="Company Docs"
        subtitle="Insurance, WorkSafeBC, CRA and other internal papers — expiry-tracked"
        actions={
          <button onClick={() => setShowAdd(s => !s)}
            className="flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90">
            <span className="material-symbols-outlined text-base">{showAdd ? 'close' : 'upload_file'}</span>
            <span className="hidden sm:inline">{showAdd ? 'Cancel' : 'Add document'}</span>
          </button>
        }
      />

      {expiring.length > 0 && (
        <div className="rounded-xl border border-warning/40 bg-warning/5 px-4 py-3">
          {expiring.map(d => {
            const days = daysUntil(d.expiry_date)!;
            return (
              <p key={d.id} className={`text-sm flex items-center gap-2 ${days < 0 ? 'text-critical' : 'text-warning'}`}>
                <span className="material-symbols-outlined text-base">warning</span>
                <span className="font-semibold">{d.title}</span>
                {days < 0 ? `expired ${-days} days ago` : `expires in ${days} days`} ({d.expiry_date})
              </p>
            );
          })}
        </div>
      )}

      {showAdd && (
        <form onSubmit={submit} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input value={fTitle} onChange={e => setFTitle(e.target.value)} placeholder="Title (e.g. WorkSafeBC clearance letter)"
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm sm:col-span-2" />
          <select value={fCategory} onChange={e => setFCategory(e.target.value as CompanyDoc['category'])}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-xs font-medium text-gray-500">Expiry</span>
            <input type="date" value={fExpiry} onChange={e => setFExpiry(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm flex-1" />
          </label>
          <input ref={fileRef} type="file" accept=".pdf,image/*,.doc,.docx,.xls,.xlsx"
            className="text-sm sm:col-span-2 file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-primary/10 file:text-primary file:font-semibold file:text-sm" />
          <input value={fNotes} onChange={e => setFNotes(e.target.value)} placeholder="Notes (optional)"
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm sm:col-span-2" />
          <div className="flex justify-end sm:col-span-2">
            <button type="submit" disabled={saving}
              className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
              {saving ? 'Uploading…' : 'File it'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-300">progress_activity</span>
        </div>
      ) : byCategory.length === 0 ? (
        <p className="text-sm text-gray-400 py-10 text-center">Nothing filed yet — add your first document.</p>
      ) : byCategory.map(cat => (
        <div key={cat.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50/60 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg text-primary">{cat.icon}</span>
            <h3 className="text-sm font-bold text-gray-900">{cat.label}</h3>
            <span className="text-xs text-gray-400">{cat.docs.length}</span>
          </div>
          <ul className="divide-y divide-gray-50">
            {cat.docs.map(d => {
              const days = daysUntil(d.expiry_date);
              return (
                <li key={d.id} className="px-5 py-2.5 flex flex-wrap items-center gap-2">
                  <button onClick={() => openDoc(d)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left group">
                    <span className="material-symbols-outlined text-xl text-gray-300 group-hover:text-primary">
                      {d.mail_message_id !== null ? 'mail' : 'draft'}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 group-hover:text-primary truncate">{d.title}</span>
                      <span className="block text-[11px] text-gray-400 truncate">
                        {[d.file_name, d.mail_message_id !== null ? 'from email' : null, d.notes].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                  {editingExpiry === d.id ? (
                    <input
                      type="date" autoFocus defaultValue={d.expiry_date ?? ''}
                      onBlur={e => saveExpiry(d, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveExpiry(d, (e.target as HTMLInputElement).value); }}
                      className="h-8 px-2 rounded-lg border border-primary text-xs"
                    />
                  ) : (
                    <button onClick={() => setEditingExpiry(d.id)}
                      title="Set / change expiry date"
                      className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                        days === null ? 'bg-gray-100 text-gray-400'
                        : days < 0 ? 'bg-critical/10 text-critical'
                        : days <= 60 ? 'bg-warning/10 text-warning'
                        : 'bg-success/10 text-success'
                      }`}>
                      {days === null ? 'no expiry' : days < 0 ? `expired` : `exp ${d.expiry_date}`}
                    </button>
                  )}
                  <button onClick={() => setConfirmDelete(d)} title="Delete"
                    className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {confirmDelete && (
        <ConfirmDialog
          message={`Remove "${confirmDelete.title}" from company docs?`}
          onConfirm={() => doDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </MainLayout>
  );
}
